import { useState, useEffect } from "react";
import { useSiteContent } from "../context/SiteContent";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import ImageCarousel from "../components/ImageCarousel";
import SeoHead from "../components/SeoHead";
import { MapPin, GraduationCap, Award, Users, Briefcase, Heart, ImageIcon, ExternalLink } from "lucide-react";

const roleIcons = [Briefcase, GraduationCap, Heart];

const resolveImgUrl = (u) => (u && u.startsWith("/")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
const LinkedName = ({ url, children, className = "" }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`no-underline hover:text-[#B8945A] transition-colors duration-200 ${className}`}>{children}</a>
  ) : (
    <span className={className}>{children}</span>
  );

const About = () => {
  const { personalInfo, currentRoles, focusSectors, skills, education, awards, memberships, images, aboutImages, quote, quotes } = useSiteContent();

  // Build the rotating quote list — support both a legacy single `quote` string
  // and a new `quotes` array of {text, author}. New array takes precedence.
  const quoteList = (Array.isArray(quotes) && quotes.length > 0)
    ? quotes.map((q) => (typeof q === "string" ? { text: q, author: "" } : { text: q.text || "", author: q.author || "" })).filter((q) => q.text)
    : (quote ? [{ text: quote, author: "" }] : []);
  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    if (quoteList.length <= 1) return;
    const t = setInterval(() => setQuoteIdx((i) => (i + 1) % quoteList.length), 5000);
    return () => clearInterval(t);
  }, [quoteList.length]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name: personalInfo.name,
      jobTitle: personalInfo.title,
      description: personalInfo.fullBio || personalInfo.shortBio,
      alumniOf: (education || []).map((e) => ({ "@type": "EducationalOrganization", name: e.institution })),
      hasOccupation: (currentRoles || []).map((r) => ({ "@type": "Role", roleName: r.title, name: r.organization })),
    },
  };

  const logoImg = (url, fallbackIcon) => {
    const Icon = fallbackIcon || ImageIcon;
    if (url) return <img src={url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt="" className="w-full h-full object-contain" />;
    return <Icon size={18} className="text-[#D5CFC7]" />;
  };

  return (
    <div>
      <SeoHead
        title="About | Abhilash Puljal — Practitioner, Not Theorist"
        description="Abhilash Puljal works inside the India-Africa and India-Asia corridors — advising on cross-border strategy, deal structuring, and market entry where the playbook does not yet exist."
        path="/about"
        type="profile"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <section className="py-20 bg-[#FAF8F4]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Profile</p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1C2B3A] mb-4">About {personalInfo.name}</h1>
          <div className="w-20 h-[2px] bg-[#B8945A]"></div>
        </div>
      </section>

      {/* Bio */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="relative overflow-hidden rounded-lg aspect-[3/4] sticky top-24">
                {aboutImages.length > 0 ? (
                  <ImageCarousel images={aboutImages} alt={personalInfo.name} testid="about-profile-carousel" />
                ) : (
                  <img src={images.diplomatic} alt={personalInfo.name} className="w-full h-full object-cover" />
                )}
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="space-y-5">
                {personalInfo.fullBio.map((p, i) => (
                  <p key={i} className="font-sans text-[#4A4A4A] leading-[1.85] text-base">{p}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote (rotates every 5s if multiple) */}
      {quoteList.length > 0 && (
        <section className="py-16 bg-[#1C2B3A]" data-testid="quote-section">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <blockquote
              key={quoteIdx}
              className="font-serif text-xl md:text-2xl text-white/90 italic leading-relaxed animate-[fadeIn_700ms_ease]"
              data-testid="quote-text"
            >
              "{quoteList[quoteIdx].text}"
            </blockquote>
            {quoteList[quoteIdx].author && (
              <p className="text-[#B8945A] text-sm font-sans tracking-wider mt-4 uppercase" data-testid="quote-author">— {quoteList[quoteIdx].author}</p>
            )}
            <div className="w-16 h-[2px] bg-[#B8945A] mx-auto mt-8"></div>
            {quoteList.length > 1 && (
              <div className="flex justify-center gap-1.5 mt-6" data-testid="quote-dots">
                {quoteList.map((_, i) => (
                  <button key={i} onClick={() => setQuoteIdx(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === quoteIdx ? "bg-[#B8945A] w-4" : "bg-white/30 hover:bg-white/60"}`} aria-label={`Quote ${i + 1}`} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Current Roles */}
      <section className="py-20 bg-[#FAF8F4]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Current Appointments</p>
          <h2 className="font-serif text-3xl font-bold text-[#1C2B3A] mb-12">Current Roles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {currentRoles.map((role, i) => {
              const FallbackIcon = roleIcons[i] || Briefcase;
              return (
                <Card key={i} className="border-[#E5DDD3] bg-white hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-7">
                    <div className="w-14 h-14 rounded-lg border border-[#E5DDD3] flex items-center justify-center flex-shrink-0 bg-[#FAF8F4] overflow-hidden mb-5">
                      {logoImg(role.logo_url, FallbackIcon)}
                    </div>
                    <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-1">{role.title}</h3>
                    <p className="font-sans text-sm text-[#B8945A] mb-1"><LinkedName url={role.url} className="text-[#B8945A]">{role.organization}</LinkedName></p>
                    <p className="font-sans text-xs text-[#7A7A7A] mb-4 flex items-center gap-1"><MapPin size={11} />{role.location}</p>
                    {role.period && <p className="font-sans text-xs text-[#7A7A7A] mb-3">{role.period}</p>}
                    <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">{role.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Focus Sectors */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Sectors</p>
          <h2 className="font-serif text-3xl font-bold text-[#1C2B3A] mb-12">Focus Sectors</h2>
          <div className="flex flex-wrap gap-3">
            {focusSectors.map((s) => (
              <Badge key={s} variant="outline" className="px-5 py-2.5 text-sm font-sans border-[#E5DDD3] text-[#4A4A4A] hover:border-[#B8945A] hover:text-[#B8945A] transition-colors duration-200 rounded-full cursor-default">{s}</Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      {skills.length > 0 && (
        <section className="py-20 bg-[#FAF8F4]">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Competencies</p>
            <h2 className="font-serif text-3xl font-bold text-[#1C2B3A] mb-12">Skills</h2>
            <div className="flex flex-wrap gap-3">
              {skills.map((s) => (
                <Badge key={s} variant="outline" className="px-5 py-2.5 text-sm font-sans border-[#B8945A]/30 text-[#1C2B3A] bg-white hover:bg-[#B8945A]/10 transition-colors duration-200 rounded-full cursor-default">{s}</Badge>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Education */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Academic Background</p>
          <h2 className="font-serif text-3xl font-bold text-[#1C2B3A] mb-12">Education</h2>
          <div className="space-y-8">
            {education.map((edu, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="w-14 h-14 rounded-lg border border-[#E5DDD3] flex items-center justify-center flex-shrink-0 bg-[#FAF8F4] overflow-hidden mt-0.5">
                  {logoImg(edu.logo_url)}
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-[#1C2B3A]">{edu.degree}</h3>
                  <p className="font-sans text-sm text-[#B8945A] mt-0.5"><LinkedName url={edu.url} className="text-[#B8945A]">{edu.institution}</LinkedName></p>
                  <p className="font-sans text-xs text-[#7A7A7A] mt-1">{edu.location} · {edu.period}</p>
                  {edu.note && <p className="font-sans text-sm text-[#4A4A4A] mt-2">{edu.note}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-20 bg-[#FAF8F4]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Recognition</p>
          <h2 className="font-serif text-3xl font-bold text-[#1C2B3A] mb-12">Awards &amp; Recognition</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {awards.map((a, i) => (
              <div key={i} className="p-7 rounded-lg border border-[#E5DDD3] bg-white hover:shadow-md transition-shadow duration-300">
                <Award size={24} className="text-[#B8945A] mb-4" />
                <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-1">{a.title}</h3>
                <p className="font-sans text-sm text-[#B8945A]">{a.institution}</p>
                <p className="font-sans text-xs text-[#7A7A7A] mt-1">{a.year}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Memberships */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Affiliations</p>
          <h2 className="font-serif text-3xl font-bold text-[#1C2B3A] mb-12">Professional Memberships</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {memberships.map((m, i) => (
              <div key={i} className="flex items-start gap-3 p-5 rounded-lg bg-[#FAF8F4] border border-[#E5DDD3] hover:border-[#B8945A]/40 transition-colors duration-200">
                <div className="w-10 h-10 rounded border border-[#E5DDD3] flex items-center justify-center flex-shrink-0 bg-white overflow-hidden">
                  {logoImg(m.logo_url, Users)}
                </div>
                <div>
                  <p className="font-sans text-sm text-[#1C2B3A] font-medium">{m.title}</p>
                  <p className="font-sans text-xs text-[#7A7A7A] mt-0.5"><LinkedName url={m.url} className="text-[#7A7A7A]">{m.organization}</LinkedName></p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
